import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface Account {
  id: string;
  name: string;
  type: 'BOLSAO' | 'VIRTUAL' | 'REAL';
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED' | 'CLOSED';
  balance: number;
  currency: string;
  entityId?: string;
  userId?: string;
  parentId?: string;
  pixKey?: string;
  pixKeyType?: string;
  bankCode?: string;
  agency?: string;
  accountNumber?: string;
  kycStatus?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AccountSummary {
  totalAccounts: number;
  totalBalance: number;
  activeAccounts: number;
  blockedAccounts: number;
  byType: {
    bolsao: number;
    virtual: number;
    real: number;
  };
}

export interface MyAccountBalance {
  accountId: string;
  balance: number;
  availableBalance: number;
  blockedBalance: number;
  currency: string;
}

// Hook para listar todas as contas do tenant
export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const response = await api.get<{ accounts: Account[]; total: number }>('/accounts');
      return response.data.accounts;
    },
  });
}

// Hook para obter uma conta específica
export function useAccount(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId],
    queryFn: async () => {
      const response = await api.get<Account>(`/accounts/${accountId}`);
      return response.data;
    },
    enabled: !!accountId,
  });
}

// Hook para obter o bolsão do tenant
export function useBolsao() {
  return useQuery({
    queryKey: ['accounts', 'bolsao'],
    queryFn: async () => {
      const response = await api.get<Account>('/accounts/bolsao');
      return response.data;
    },
  });
}

// Hook para obter resumo das contas (Admin)
export function useAccountsSummary() {
  return useQuery({
    queryKey: ['accounts', 'summary'],
    queryFn: async () => {
      const response = await api.get<AccountSummary>('/accounts/summary');
      return response.data;
    },
  });
}

// Hook para obter minha conta (Correntista)
export function useMyAccount() {
  return useQuery({
    queryKey: ['accounts', 'my-account'],
    queryFn: async () => {
      const response = await api.get<MyAccountBalance>('/accounts/my-account');
      return response.data;
    },
  });
}

// Hook para obter saldo de uma conta
export function useAccountBalance(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'balance'],
    queryFn: async () => {
      const response = await api.get<{ balance: string }>(`/accounts/${accountId}/balance`);
      return parseInt(response.data.balance, 10);
    },
    enabled: !!accountId,
  });
}

// Hook para obter contas de uma entidade
export function useEntityAccounts(entityId: string) {
  return useQuery({
    queryKey: ['accounts', 'entity', entityId],
    queryFn: async () => {
      const response = await api.get<{ accounts: Account[]; total: number }>(`/accounts/entity/${entityId}`);
      return response.data.accounts;
    },
    enabled: !!entityId,
  });
}

// Hook para obter contas de um usuário
export function useUserAccounts(userId: string) {
  return useQuery({
    queryKey: ['accounts', 'user', userId],
    queryFn: async () => {
      const response = await api.get<Account[]>(`/accounts/user/${userId}`);
      return response.data;
    },
    enabled: !!userId,
  });
}

// Hook para obter contas filhas
export function useChildAccounts(parentId: string) {
  return useQuery({
    queryKey: ['accounts', 'children', parentId],
    queryFn: async () => {
      const response = await api.get<{ accounts: Account[]; total: number }>(`/accounts/${parentId}/children`);
      return response.data.accounts;
    },
    enabled: !!parentId,
  });
}

// Mutations

export interface CreateVirtualAccountDto {
  name: string;
  entityId?: string;
  userId?: string;
  parentId?: string;
}

export interface CreateRealAccountDto {
  name: string;
  entityId?: string;
  userId?: string;
  bankCode: string;
  agency: string;
  accountNumber: string;
}

export interface CreateBolsaoDto {
  name: string;
}

export function useCreateVirtualAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateVirtualAccountDto) => {
      const response = await api.post<Account>('/accounts/virtual', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useCreateRealAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRealAccountDto) => {
      const response = await api.post<Account>('/accounts/real', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useCreateBolsao() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBolsaoDto) => {
      const response = await api.post<Account>('/accounts/bolsao', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Account> }) => {
      const response = await api.patch<Account>(`/accounts/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account', variables.id] });
    },
  });
}

export function useUpdateAccountStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Account['status'] }) => {
      const response = await api.patch<Account>(`/accounts/${id}/status`, { status });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account', variables.id] });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/accounts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
